import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";

import Spinner from "../../../../reusableComponents/spinner/spinner";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/hooks";
import { Program } from "../../../../../models/program";
import { RoleIndex } from "../../../../../models/role";
import { UserType } from "../../../../../models/userType";
import {
  addTrainingUser,
  addUser,
  editTrainingUser,
  editUser,
  getClientUserTitle,
} from "../../../../../store/thunks/systemManagementThunk";
import {
  getBranchesDepartments,
  getClientPrograms,
  getClientSalesRep,
  getTrainingDomains,
  getTrainingSchools,
  getTrainingUsers,
} from "../../../../../store/thunks/portalManagementThunk";

import IdentitySection from "./sections/identitySection";
import ProgramAccessSection from "./sections/programAccessSection";
import AnalyticsProfile from "./sections/analyticsProfile";
import ProgramTypeSection from "./sections/programTypeSection";
import TrainingProfile from "./sections/trainingProfile";
import {
  UserFormValues,
  emptyUserForm,
  hasTitleOnly,
  isAnalyticsType,
  isTrainingType,
  programForClientType,
  userValidationSchema,
} from "./userDialogModel";
import {
  buildCreateAnalyticsPayload,
  buildCreateTrainingPayload,
  buildEditAnalyticsPayload,
  buildEditTrainingPayload,
  isTrainingRow,
  isTrainingUser,
  nameChanged,
  profileChanged,
  trainingScopeChanged,
} from "./payloads";

// Distinct domains, derived the same way the training dialogs derive them.
const domainsFrom = (trainingDomains: any[]): string[] =>
  Array.from(
    new Set(
      (trainingDomains || [])
        .map((entry: any) => entry.email?.split("@")[1]?.split(".")[0])
        .filter(Boolean)
    )
  );

export interface UserDialogProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: (message?: string) => void;
  /** Row from /api/users. Required in edit mode. */
  editUserData?: any;
}

const UserDialog = ({ open, mode, onClose, editUserData }: UserDialogProps) => {
  const dispatch = useAppDispatch();

  const {
    allClients,
    allRoles,
    clientType,
    clientUserTitle,
    addUserLoading,
    addTrainingUserLoading,
    editTrainingUserLoading,
    getClientUserTitleLoading,
  } = useAppSelector((state: any) => state.systemManagement);

  const {
    branches,
    departments,
    getBranchesDepartmentsLoading,
    trainingSchools,
    trainingDomains,
    trainingUsers,
  } = useAppSelector((state: any) => state.portalManagement);

  const isEdit = mode === "edit";
  // Guard the submit while a save is in flight, as the training dialogs do.
  // Without it the button stays live and a second click fires a second request.
  const saving = Boolean(addUserLoading || addTrainingUserLoading || editTrainingUserLoading);

  const [clientPrograms, setClientPrograms] = useState<number[]>([]);
  const [salesReps, setSalesReps] = useState<any[]>([]);
  const [branchesReady, setBranchesReady] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const form = useFormik<UserFormValues>({
    initialValues: emptyUserForm,
    validationSchema: userValidationSchema,
    enableReinitialize: false,
    onSubmit: (values) => submit(values),
  });

  const roleIndex = form.values.user_role?.role_index;
  const clientTypeIndex = form.values.client_type?.client_type_index;

  // Backs the scope we prefill from and diff against.
  const trainingRecord = useMemo(() => {
    if (!isEdit || !editUserData) return undefined;
    return (trainingUsers || []).find(
      (row: any) => row.clients_user_id === editUserData.client_user_index
    );
  }, [isEdit, editUserData, trainingUsers]);

  // Offer a client type only when the selected client has that type's program.
  // One rule, driven by Program.AllowedUserTypesIndex: the old dialog gated
  // Sales_Rep alone, so Manager was offered for every client — including
  // training-only ones like LWS, which have no title, branch or department.
  const clientTypeOptions = useMemo(() => {
    const all = clientType || [];
    if (!clientPrograms.length) return all;
    return all.filter((type: any) => {
      const program = programForClientType(type.client_type_index);
      return program === undefined ? true : clientPrograms.includes(program);
    });
  }, [clientType, clientPrograms]);

  const domains = useMemo(() => domainsFrom(trainingDomains), [trainingDomains]);

  // The API returns some titles twice — same name, different key (Service,
  // Parts and Rental Manager today). Two identical labels are indistinguishable
  // to whoever is choosing, and they collide as React keys. First one wins.
  const titleOptions = useMemo(() => {
    const seen = new Set<string>();
    return (clientUserTitle || []).filter((title: any) => {
      const name = title?.client_title_name;
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [clientUserTitle]);

  // The client type IS the program grant, so nothing program-specific renders
  // until it is chosen. Showing analytics fields first meant a training-only
  // client like LWS offered Title/Branch/Department and then took them away.
  const analyticsProfileVisible =
    roleIndex === RoleIndex.ClientUser &&
    Boolean(form.values.client) &&
    isAnalyticsType(clientTypeIndex);

  const showClientType = roleIndex === RoleIndex.ClientUser && Boolean(form.values.client);
  const showTrainingProfile =
    roleIndex === RoleIndex.ClientUser && isTrainingType(clientTypeIndex);

  const loadClientContext = (client: any) => {
    if (!client) {
      setClientPrograms([]);
      setBranchesReady(false);
      return;
    }
    dispatch(getClientPrograms({ client_index: client.client_index })).then(
      (result: any) => {
        const programs = (result.payload?.programs || []).map(
          (program: any) => program.program_index
        );
        setClientPrograms(programs);
      }
    );
    dispatch(
      getBranchesDepartments({ payload: { customer_id: client.customer_id } })
    ).then(() => setBranchesReady(true));
  };

  const loadTypeContext = (type: any, client: any) => {
    const index = type?.client_type_index;
    if (index === UserType.Index.SalesRep && client) {
      dispatch(getClientSalesRep({ customer_id: client.customer_id })).then(
        (result: any) => setSalesReps(result.payload?.salesmen_list || [])
      );
    }
    if (index === UserType.Index.LWS_SCHOOL) dispatch(getTrainingSchools({}));
    if (index === UserType.Index.LWS_DEALER) dispatch(getTrainingDomains({}));
  };

  const handleRoleChange = (role: any) => {
    setSubmitError("");
    // Everything below belongs to the role just replaced.
    form.setFieldValue("client", undefined);
    form.setFieldValue("clients", undefined);
    form.setFieldValue("client_type", undefined);
    form.setFieldValue("client_title", undefined);
    form.setFieldValue("client_branch", undefined);
    form.setFieldValue("client_department", undefined);
    form.setFieldValue("sale_rep", undefined);
    form.setFieldValue("user_school", undefined);
    form.setFieldValue("user_domain", undefined);
    setClientPrograms([]);
    setSalesReps([]);
    setBranchesReady(false);
    if (role?.role_index === RoleIndex.AccountAdmin) dispatch(getClientUserTitle({}));
  };

  const handleClientChange = (client: any) => {
    setSubmitError("");
    form.setFieldValue("client_type", undefined);
    form.setFieldValue("client_branch", undefined);
    form.setFieldValue("client_department", undefined);
    form.setFieldValue("sale_rep", undefined);
    setSalesReps([]);
    setBranchesReady(false);
    dispatch(getClientUserTitle({}));
    loadClientContext(client);
  };

  const handleClientTypeChange = (type: any) => {
    setSubmitError("");
    // Scope fields belong to one program each.
    form.setFieldValue("sale_rep", undefined);
    form.setFieldValue("user_school", undefined);
    form.setFieldValue("user_domain", undefined);
    setSalesReps([]);
    loadTypeContext(type, form.values.client);
  };

  const submit = (values: UserFormValues) => {
    setSubmitError("");

    const finish = (result: any) => {
      if (result.payload?.result === true) {
        onClose(result.payload.message);
      } else {
        setSubmitError(result.payload?.message || "The change could not be saved.");
      }
    };

    if (!isEdit) {
      const training = isTrainingUser(values);
      const action = training
        ? addTrainingUser({ createUserPayload: buildCreateTrainingPayload(values) })
        : addUser({ createUserPayload: buildCreateAnalyticsPayload(values) });
      dispatch(action as any).then(finish);
      return;
    }

    const changedName = nameChanged(values, editUserData);

    // Training-typed before or after: the training resource owns the change.
    if (isTrainingUser(values) || isTrainingRow(editUserData)) {
      const changedRoleType =
        values.client_type?.client_type_index !== editUserData?.client_type_index;
      const changedScope = trainingScopeChanged(values, trainingRecord);
      if (!changedName && !changedRoleType && !changedScope) {
        setSubmitError("No Changes have been made");
        return;
      }
      dispatch(
        editTrainingUser({
          editUserPayload: buildEditTrainingPayload(values, editUserData, trainingRecord, {
            name: changedName,
            roleType: changedRoleType,
            scope: changedScope,
          }),
        })
      ).then(finish);
      return;
    }

    const changedProfile = profileChanged(values, editUserData);
    if (!changedName && !changedProfile) {
      setSubmitError("No Changes have been made");
      return;
    }
    dispatch(
      editUser({
        editUserPayload: buildEditAnalyticsPayload(values, editUserData, {
          name: changedName,
          profile: changedProfile,
        }),
      })
    ).then(finish);
  };

  // Reset on open so a previous user's values cannot leak into the next.
  useEffect(() => {
    if (!open) return;
    setSubmitError("");
    setClientPrograms([]);
    setSalesReps([]);
    setBranchesReady(false);
    form.resetForm({ values: emptyUserForm });
    if (!isEdit) return;
    if (!editUserData) return;

    const role = {
      role: editUserData.role,
      role_index: editUserData.role_index,
      role_label: editUserData.role_label,
    };
    const client = editUserData.client_list?.[0];
    const training = isTrainingRow(editUserData);

    form.setValues({
      ...emptyUserForm,
      email: editUserData.user_name || "",
      first_name: editUserData.first_name || "",
      last_name: editUserData.last_name || "",
      user_role: role,
      client: editUserData.role_index === RoleIndex.ClientUser ? client : undefined,
      clients:
        editUserData.role_index === RoleIndex.ClientUser
          ? undefined
          : editUserData.client_list,
      client_type:
        editUserData.role_index === RoleIndex.ClientUser
          ? {
              client_type_index: editUserData.client_type_index,
              client_type_name: editUserData.client_type_name,
              label: editUserData.client_type_label,
            }
          : undefined,
    });

    dispatch(getClientUserTitle({})).then((result: any) => {
      const match = (result.payload?.client_user_title || []).find(
        (title: any) => title.client_title_key === editUserData.client_title
      );
      if (match) form.setFieldValue("client_title", match);
    });

    if (editUserData.role_index === RoleIndex.ClientUser && client) {
      loadClientContext(client);
      if (editUserData.client_type_index === UserType.Index.SalesRep) {
        dispatch(getClientSalesRep({ customer_id: client.customer_id })).then(
          (result: any) => {
            const list = result.payload?.salesmen_list || [];
            setSalesReps(list);
            // The old dialog wrote this to "sale", so it never prefilled.
            const match = list.find(
              (rep: any) => rep.SALESMAN === editUserData.salemen_name
            );
            if (match) form.setFieldValue("sale_rep", match);
          }
        );
      }
      if (editUserData.client_branch) {
        form.setFieldValue("client_branch", {
          label: editUserData.client_branch,
          value: editUserData.client_branch,
        });
      }
      if (editUserData.client_department) {
        form.setFieldValue("client_department", {
          label: editUserData.client_department,
          value: editUserData.client_department,
        });
      }
    }

    if (training) {
      dispatch(getTrainingUsers({}));
      dispatch(getTrainingSchools({}));
      dispatch(getTrainingDomains({}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editUserData, isEdit]);

  // Prefillable only once the training record has loaded.
  useEffect(() => {
    if (!isEdit || !trainingRecord) return;
    if (editUserData?.client_type_index === UserType.Index.LWS_SCHOOL) {
      const school = (trainingSchools || []).find(
        (item: any) => item.name === trainingRecord.r_operand
      );
      form.setFieldValue("user_school", school || { name: trainingRecord.r_operand });
    }
    if (editUserData?.client_type_index === UserType.Index.LWS_DEALER) {
      form.setFieldValue("user_domain", trainingRecord.r_operand);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, trainingRecord, trainingSchools, editUserData]);

  const handleClose = (_?: any, reason?: string) => {
    if (reason === "backdropClick") return;
    onClose();
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="md"
      onClose={handleClose}
      open={open}
      disableEscapeKeyDown={true}
    >
      <DialogTitle
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "rgba(59, 147, 74, 1)",
        }}
      >
        {/* component="span": DialogTitle renders an h2, and an h5 inside it is
            invalid DOM nesting — React warns at runtime. */}
        <Typography variant="h5" component="span" sx={{ fontWeight: "bold", color: "white" }}>
          {isEdit ? "Edit User" : "Add User"}
        </Typography>
        <IconButton aria-label="close" onClick={() => onClose()} sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Spinner loading={addUserLoading} />
      <Spinner loading={addTrainingUserLoading} />
      <Spinner loading={editTrainingUserLoading} />
      <Spinner loading={getBranchesDepartmentsLoading} />
      <Spinner loading={getClientUserTitleLoading} />
      <form onSubmit={form.handleSubmit}>
        <DialogContent>
          <DialogContentText>
            <IdentitySection form={form} lockEmail={isEdit} />

            <ProgramAccessSection
              form={form}
              roles={allRoles}
              clients={allClients}
              onRoleChange={handleRoleChange}
              onClientChange={handleClientChange}
            />

            {showClientType && (
              <ProgramTypeSection
                form={form}
                clientTypes={clientTypeOptions}
                salesReps={salesReps}
                showSalesRep={isAnalyticsType(clientTypeIndex)}
                onClientTypeChange={handleClientTypeChange}
              />
            )}

            <AnalyticsProfile
              form={form}
              clientUserTitle={titleOptions}
              branches={branches}
              departments={departments}
              showTitle={hasTitleOnly(roleIndex) || analyticsProfileVisible}
              showBranchDepartment={analyticsProfileVisible && branchesReady}
            />

            {showTrainingProfile && (
              <TrainingProfile form={form} schools={trainingSchools} domains={domains} />
            )}

            {submitError && (
              <Grid sx={{ display: "flex", justifyContent: "center" }}>
                <Typography sx={{ color: "red" }}>{submitError}</Typography>
              </Grid>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid" }}>
          <Button
            variant="outlined"
            onClick={() => onClose()}
            sx={{ padding: "5px 10px 5px 10px", backgroundColor: "red", color: "black" }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="outlined"
            disabled={saving}
            sx={{
              padding: "5px 10px 5px 10px",
              backgroundColor: "rgba(59, 147, 74, 1)",
              color: "white",
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserDialog;
